// This file containing mutation functions for updating data using React Query

import { useMutation, useQueryClient } from "@tanstack/react-query"

import * as API from "./api"
import {
    addFailureCodeList,
    addGgIndicatorList,
    addInternalUserList,
    addMasterMappingTMC,
    addNewParticipant,
    addPriceList,
    addPriceMatrixException,
    addPsIndicatorList,
    addLumsumPrice,
    addRegistrationData,
    addToActionCodeList,
    addToExceptionCodeList,
    addToProductFromList,
    addTrainingSchedule,
    createAllowedMaterials,
    createApprover,
    createArticle,
    createAssociation,
    createAssociationFGWithCountry,
    createAssociationForServiceMaterial,
    createBlockBullitenToCountry,
    createBlockTypeNoToCountry,
    createComplaintsWithoutTechnicalDefects,
    createCountryConfiguration,
    createCountryHub,
    createExchangeRate,
    createFgPartNoAssociationApi,
    createFinsihedGood,
    createHubContinent,
    createHubRegContinent,
    createHubSubContinent,
    createOrderMinMax,
    createOrderParts,
    createPartNumber,
    createPartTypeAssociationApi,
    CreatePerformanceConfig,
    createPriceMatrixApi,
    createRecordList,
    createSalesOrg,
    createServiceMaterialAssociationList,
    createServiceMaterialAssociationSalesOrg,
    createServiceMaterialAssociationTranslations,
    createTypeNumber,
    deletAssociatepartOfType,
    deletAssociation,
    deletBlockBulletin,
    deletBlockType,
    deletCountryHubRecord,
    deleteAllOrderCartViews,
    deleteAllowedMaterials,
    deleteArticleSeries,
    deleteAssociationForServiceMaterial,
    deleteAssociationOfFgWithParts,
    deleteComplaintsWithoutTechnicalDefects,
    deleteOrderCartViewById,
    deleteProductLifecycle,
    downloadActionCodeList,
    downloadAllowedMaterials,
    downloadArticleSeries,
    downloadAssociatedFgList,
    downloadAssociatedPartList,
    downloadAssociatePartToType,
    downloadAssociation,
    downloadComplaintsWithoutTechnicalDefectsList,
    downloadCountryConfigRecords,
    downloadCountryHubRecord,
    downloadExceptionApproverCodeList,
    downloadExceptionCodeList,
    downloadExchangeRateRecord,
    downloadFailureCodeList,
    downloadFGAssociationCountryList,
    downloadFinishedGoodsList,
    downloadGgIndicatorList,
    downloadLifeCycleStatusList,
    downloadOverViewProductList,
    downloadParticipantRecords,
    downloadPartsList,
    downloadPFPReport,
    downloadPriceDerivation,
    downloadPriceList,
    downloadPriceMatrixExceptionList,
    downloadPriceMatrixList,
    downloadProductFormList,
    downloadPsIndicatorList,
    downloadServiceCenterRecord,
    downloadServiceMaterialAssociation,
    downloadTypeNumberList,
    filterAssociatedFgList,
    filterAssociatedPartList,
    getFinishedGoodsListAll,
    getHubDetails,
    getHubRegDetails,
    getHubSubDetails,
    getInterUser,
    getSalesOrgDetails,
    getScheduledParitcipants,
    getUser,
    handleAPIRequests,
    postAllComments,
    saveUploadedData,
    updateActionCodeList,
    updateActiveStatus,
    updateAllowedMaterials,
    updateApprover,
    updateArticleSeries,
    updateAssociatePartToTypeApi,
    updateAssociation,
    updateAssociationForServiceMaterial,
    updateComplaintsWithoutTechnicalDefects,
    updateCountryConfiguration,
    updateCountryHub,
    updateExceptionCodeList,
    updateExchangeRate,
    updateExternalUsersList,
    updateFailureCodeList,
    updateFinishedGoodsByID,
    updateGgIndicatorList,
    updateHubContinent,
    updateHubRegContinent,
    updateHubSubContinent,
    updateInternalUsersList,
    updateMasterMappingTMC,
    updateNotificationAsRead,
    updateOrderMinMax,
    updateOrderParts,
    updatePartDetails,
    updateParticipantCompletion,
    updatePerformanceConfig,
    UpdatePriceDerivation,
    updatePriceList,
    updatePriceMatrixApi,
    updatePriceMatrixException,
    updateProductFromList,
    updatePsIndicatorList,
    updateReadRecipient,
    updateSalesOrg,
    updateServiceCenter,
    updateServiceMatrialAssociations,
    updateTrainingCompletionStatus,
    updateTypeNumber,
    updateWorkListApproval,
    uploadCartViews,
    validateBasket,
    validateEANandArticle} from "./api"

export const useAddRolesAndPermission = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (data) => handleAPIRequests({ queryKey: ["POST", "user", "role-permissions", data] }),
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["addrolespermission"] })
        },
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
                return error
            } else {
                await queryClient.invalidateQueries({
                    queryKey: ["addrolespermission"]
                })
            }
        }
    })
}

export const useUpdateRolesAndPermission = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (data) => {
            return handleAPIRequests({ queryKey: ["PATCH", "user", `role-permissions/${data.id}`, data] })
        },
        onMutate: () => { },
        onError: () => { },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["updaterolespermission"] })
        },
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
                return error
            } else {
                await queryClient.invalidateQueries({
                    queryKey: ["updaterolespermission"]
                })
            }
        }
    })
}

export function useUpdatePsIndicatorList() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ id, data }) => {
            return await updatePsIndicatorList(id, data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => console.log("Update is done"),
        onSuccess: () => {
        },
        onError: () => console.log("error occured while updating"),
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            } else {
                await queryClient.invalidateQueries({ queryKey: ["psList"] })
            }
        }
    })
}

export function useUpdateFailureCodeList() {
    return useMutation({
        mutationFn: async ({ id, data }) => {
            return await updateFailureCodeList(id, data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => console.log("Update is done"),
        onSuccess: () => {
        },
        onError: () => console.log("error occured while updating"),
        onSettled: async () => {
        }
    })
}

export function useUpdateGgIndicatorList() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ id, data }) => {
            return await updateGgIndicatorList(id, data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => console.log("Update is done"),
        onSuccess: () => {
        },
        onError: () => console.log("error occured while updating"),
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            } else {
                await queryClient.invalidateQueries({ queryKey: ["ggusers"] })
            }
        }
    })
}

export function useAddPsIndicatorList() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (data) => addPsIndicatorList(data),
        onMutate: () => { },
        onError: () => { },
        onSuccess: () => {
            //queryClient.invalidateQueries({ queryKey: ["psList"] })
        },
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            } else {
                await queryClient.invalidateQueries({ queryKey: ["users"] })
            }
        }
    })
}

export function useAddLumsumPrice() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (data) => addLumsumPrice(data),
        onMutate: () => { },
        onError: () => { },
        onSuccess: () => {
        },
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            } else {
                await queryClient.invalidateQueries({ queryKey: ["users"] })
            }
        }
    })
}

export function useAddFailureCodeList() {
    return useMutation({
        mutationFn: (data) => addFailureCodeList(data),
        onMutate: () => { },
        onError: () => { },
        onSuccess: () => { },
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            } else {
                //await queryClient.invalidateQueries({ queryKey: ["failureList"] })
            }
        }
    })
}

export function useAddGgIndicatorList() {
    return useMutation({
        mutationFn: (data) => addGgIndicatorList(data),
        onMutate: () => { },
        onError: () => { },
        onSuccess: () => { },
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            } else {
                //await queryClient.invalidateQueries({ queryKey: ["ggusers"] })
            }
        }
    })
}

export const CreateHubList = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (body) => {
            return await createHubContinent(body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                queryClient.invalidateQueries({ queryKey: ["getHubList"] })
            }
            if (data) {
                return data
            }
        }
    })
}

export const UpdateHubList = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await updateHubContinent(payload.id, payload.body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                queryClient.invalidateQueries({ queryKey: ["getHubList"] })
            }
            if (data) {
                return data
            }
        }
    })
}

export const CreateHubRegList = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (body) => {
            return await createHubRegContinent(body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                queryClient.invalidateQueries({ queryKey: ["getHubRegList"] })
            }
            if (data) {
                return data
            }
        }
    })
}

export const UpdateRegHubList = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await updateHubRegContinent(payload.id, payload.body).catch(
                (ex) => {
                    throw ex.response ? ex.response.data : ex.message
                }
            )
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                queryClient.invalidateQueries({ queryKey: ["getHubRegList"] })
            }
            if (data) {
                return data
            }
        }
    })
}

export const CreateSubHubList = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (body) => {
            return await createHubSubContinent(body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                queryClient.invalidateQueries({ queryKey: ["getHubSubList"] })
            }
            if (data) {
                return data
            }
        }
    })
}

export const UpdateSubHubList = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await updateHubSubContinent(payload.id, payload.body).catch(
                (ex) => {
                    throw ex.response ? ex.response.data : ex.message
                }
            )
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                queryClient.invalidateQueries({ queryKey: ["getHubSubList"] })
            }
            if (data) {
                return data
            }
        }
    })
}

export const useCreateOrderParts = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (body) => {
            return await createOrderParts(body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                queryClient.invalidateQueries({ queryKey: ["listOrderCartView"] })
            }
            if (data) {
                return data
            }
        }
    })
}

export const useUpdateOrderParts = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await updateOrderParts(payload.id, payload.body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                queryClient.invalidateQueries({ queryKey: ["listOrderCartView"] })
            }
            if (data) {
                return data
            }
        }
    })
}

export const useDeleteOrderCartViewById = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (id) => {
            return await deleteOrderCartViewById(id)
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["listOrderCartView"] })
        },
        retry: 1,
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }
        }
    })
}

export const useDeleteOrderUploadById = () => {
    //const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (orderId) => {
            return await API.deleteOrderUploadById(orderId)
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => {
            //queryClient.invalidateQueries({ queryKey: ["listOrderCartView"] })
        },
        retry: 1,
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }
        }
    })
}

export const useDeleteAllOrderCartViews = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await deleteAllOrderCartViews(payload)
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["listOrderCartView"] })
        },
        retry: 1,
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }
        }
    })
}

export const useUploadCartViews = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (fileData) => {
            return await uploadCartViews(fileData).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: (async (data, error) => {
            if (error) {
                throw error
            } else {
                queryClient.invalidateQueries({ queryKey: ["listOrderCartView"] })
            }
            if (data) {
                return data
            }
        }),
        onSuccess: () => { },
        retry: 1
    })
}

export const useGetValidateBasket = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (fileData) => {
            return await validateBasket(fileData).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: (async (data, error) => {
            if (error) {
                throw error
            } else {
                queryClient.invalidateQueries({ queryKey: ["listOrderCartView"] })
            }
            if (data) {
                return data
            }
        }),
        onSuccess: () => { },
        retry: 1
    })
}

export const useSendOrder = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (body) => {
            return await API.sendOrder(body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            else {
                queryClient.invalidateQueries({ queryKey: ["listOrderCartView"] })
            }
            if (data) {
                return data
            }
        }
    })
}

export const useSendOrderUpload = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (body) => {
            return await API.sendOrderUpload(body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            else {
                queryClient.invalidateQueries({ queryKey: ["listOrderCartView"] })
            }
            if (data) {
                return data
            }
        }
    })
}

export const CreateSalesOrgData = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (body) => {
            return await createSalesOrg(body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["getSalesOrg", 1, 10, "org_code", "DESC", ""]
            })
        },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                queryClient.invalidateQueries({ queryKey: ["getSalesOrg"] })
            }
            if (data) {
                return data
            }
        }
    })
}

export const UpdateSalesOrgData = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await updateSalesOrg(payload.id, payload.body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                queryClient.invalidateQueries({ queryKey: ["getSalesOrg"] })
            }
            if (data) {
                return data
            }
        }
    })
}

export const CreateExchangeRate = () => {
    return useMutation({
        mutationFn: async (body) => {
            return await createExchangeRate(body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                //queryClient.invalidateQueries({ queryKey: ["getExchangeRateList"] })
            }
            if (data) {
                return data
            }
        }
    })
}

export const UpdateExchangeRate = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await updateExchangeRate(payload.id, payload.body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getExchangeRate"] })
        },
        retry: 1,
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }
        }
    })
}
export function useUpdatePsIndicator() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (data) => {
            updatePsIndicatorList(data)
        },
        onMutate: () => console.log("Update is done"),
        onError: () => console.log("error occured while updating"),
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: ["psList"] })
        }
    })
}

export function useUpdateFailureCode() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (data) => {
            updateFailureCodeList(data)
        },
        onMutate: () => console.log("Update is done"),
        onError: () => console.log("error occured while updating"),
        onSettled: async () => {
            await queryClient.invalidateQueries({ queryKey: ["failureList"] })
        }
    })
}

export function useUpdatePriceList() {
    return useMutation({
        mutationFn: async (data) => {
            await updatePriceList(data)
        },
        onMutate: () => console.log("Update is done"),
        onError: () => console.log("error occured while updating"),
        onSettled: async () => {
            //await queryClient.invalidateQueries({ queryKey: ["priceList"] })
        }
    })
}

export function useAddPriceList() {
    //const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (data) => await addPriceList(data),
        onMutate: () => console.log("Update is done"),
        onError: () => {
            console.log("error occured while updating")
        },
        onSettled: async () => {
            /*await queryClient.invalidateQueries({
                queryKey: ["priceList", page, limit]
            })*/
        }
    })
}

export const CreateApprovers = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (data) => createApprover(data),
        onMutate: () => { },
        onError: () => { },
        onSuccess: () => { },
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            } else {
                await queryClient.invalidateQueries({
                    queryKey: ["getExceptionApproverList"]
                })
            }
        }
    })
}

export const UpdateApprovers = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ id, data }) => {
            return await updateApprover(id, data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getApprovers"] })
            queryClient.invalidateQueries({ queryKey: ["getOrderMinMaxList"] })
        },
        onError: () => {
            console.log("error")
        },
        retry: 1,
        onSettled: async () => {
        }
    })
}

export const CreateSalesMinMaxData = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (body) => {
            return await createOrderMinMax(body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: (async (data, error) => {
            if (error) {
                throw error
            } else {
                queryClient.invalidateQueries({ queryKey: ["GetOrderMinMax"] })
            }
            if (data) {
                return data
            }
        }),
        onSuccess: () => { },
        retry: 1
    })
}

export const ValidateEANandArticle = () => {
    return useMutation({
        mutationFn: async (body) => {
            return await validateEANandArticle(body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: (async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }),
        onSuccess: () => { },
        retry: 1
    })
}

export const UpdateSalesMinMaxData = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await updateOrderMinMax(payload.id, payload.body, payload.method).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: (async (data, error) => {
            if (error) {
                throw error
            } else {
                queryClient.invalidateQueries({ queryKey: ["GetOrderMinMax"] })
            }
            if (data) {
                return data
            }
        }),
        onSuccess: () => { },
        retry: 1
    })
}

//Exception code

export function useUpdateExceptionCodeList() {
    return useMutation({
        mutationFn: async ({ id, data }) => {
            return await updateExceptionCodeList(id, data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => console.log("Update is done"),
        onSuccess: async () => {
        },
        onError: () => console.log("error occured while updating"),
        onSettled: async () => {
        }
    })
}

export function useAddExceptionCodeList() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (data) => addToExceptionCodeList(data),
        onMutate: () => { },
        onError: () => { },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["getExceptionCodeList"]
            })
        },
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            } else {
                /*await queryClient.invalidateQueries({
                    queryKey: ["getExceptionCodeList"]
                })*/
            }
        }
    })
}

export function useUpdateProductFromList() {
    return useMutation({
        mutationFn: async ({ id, data }) => {
            return await updateProductFromList(id, data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => console.log("Update is done"),
        onSuccess: async () => {
        },
        onError: (e) => console.log("error occured while updating", e),
        onSettled: async () => {
        }
    })
}

export function useAddProductFromList() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (data) => addToProductFromList(data),
        onMutate: () => { },
        onError: (e) => { console.log(e) },
        onSuccess: () => { },
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            } else {
                await queryClient.invalidateQueries({
                    queryKey: ["getProductFromList"]
                })
            }
        }
    })
}
// Create Country Hub Asociation
export const CreateCountryHubAssociation = () => {
    return useMutation({
        mutationFn: async (body) => {
            return await createCountryHub(body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

// Update Country Hub Asociation
export const UpdateCountryHubRecord = () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await updateCountryHub(payload.id, payload.data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const DeleteCountryHubRecord = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (id) => {
            await deletCountryHubRecord(id)
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["countryHubALL"] })
        },
        retry: 3,
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }
        }
    })
}

export function useUpdateActionCodeList() {
    return useMutation({
        mutationFn: async ({ id, data }) => {
            return await updateActionCodeList(id, data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => console.log("Update is done"),
        onSuccess: async () => {
        },
        onError: (e) => console.log("error occured while updating", e),
        onSettled: async () => {
        }
    })
}

export function useAddActionCodeList() {
    return useMutation({
        mutationFn: (data) => addToActionCodeList(data),
        onMutate: () => { },
        onError: () => {
        },
        onSuccess: () => {
            //queryClient.invalidateQueries({ queryKey: ["getActionCodeList"] })
        },
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            } else {
                // await queryClient.invalidateQueries({ queryKey: ["users"] })
            }
        }
    })
}

export const GetHubListALL = () => {
    return useMutation({
        mutationFn: async (search) => {
            return await getHubDetails({
                queryKey: ["GetHubListALL", { search }]
            }).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const DownloadHubCountryRecord = () => {
    return useMutation({
        mutationFn: async () => {
            return await downloadCountryHubRecord().catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const DownloadExchangeRateMutationRecord = (search, appliedFilter, orderBy, orderByField) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadExchangeRateRecord(search, appliedFilter, orderBy, orderByField).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

// Create  Failure Code Association
export const CreateFailureAssociation = () => {
    return useMutation({
        mutationFn: async (body) => {
            return await createAssociation(body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } 
            if (data) {
                return data
            }
        }
    })
}

// Update Failure Code Association
export const UpdateFailureAssociation = () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await updateAssociation(payload.id, payload.data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } 
            if (data) {
                return data
            }
        }
    })
}

export const DeleteFailureAssociation = () => {
    return useMutation({
        mutationFn: async (id) => {
            return await deletAssociation(id)
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => {
            console.log("success")
        },
        retry: 3,
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }
        }
    })
}

export const DownloadAssociation = () => {
    return useMutation({
        mutationFn: async () => {
            return await downloadAssociation().catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const GetHubRegionalListALL = () => {
    return useMutation({
        mutationFn: async (search) => {
            return await getHubRegDetails({
                queryKey: ["getHubRegList", search]
            }).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const DownloadCountryConfig = () => {
    return useMutation({
        mutationFn: async () => {
            return await downloadCountryConfigRecords().catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const GetSalesOrgListALL = () => {
    return useMutation({
        mutationFn: async (search) => {
            return await getSalesOrgDetails({
                queryKey: ["GetSalesOrgListALL", { search }]
            }).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const GetHubSubContinantallListALL = () => {
    return useMutation({
        mutationFn: async (search) => {
            return await getHubSubDetails({
                queryKey: ["GetHubSubContinantallListALL", { search }]
            }).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const CreateCountryConfiguration = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (body) => {
            return await createCountryConfiguration(body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                queryClient.invalidateQueries({
                    queryKey: ["getCountryConfigList"]
                })
            }
            if (data) {
                return data
            }
        }
    })
}

export const UpdateCountryConfiguration = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (data) => {
            return await updateCountryConfiguration(data.id, data.payload).catch(
                (ex) => {
                    throw ex.response ? ex.response.data : ex.message
                }
            )
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                queryClient.invalidateQueries({
                    queryKey: ["getCountryConfigList"]
                })
            }
            if (data) {
                return data
            }
        }
    })
}

export const useUpdatePriceMatrix = () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await updatePriceMatrixApi(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                return error
            }
            if (data) {
                return data
            }
        }
    })
}

export const useCreatePriceMatrix = (page, limit) => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => await createPriceMatrixApi(payload),
        onMutate: () => { },
        onError: () => {
            // throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["priceMatrixList"]
            })
        },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                // throw error;
            }
            if (data) {
                await queryClient.invalidateQueries({
                    queryKey: ["priceMatrixList", page, limit]
                })
            }
        }
    })
}

export function useAddRegistrationData() {
    return useMutation({
        mutationFn: async (data) => {
            return await addRegistrationData(data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        retry: 0
    })
}

export const useUpdateExternalUsersList = () => {
    return useMutation({
        mutationFn: async ({ id, data }) => {
            return await updateExternalUsersList(id, data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => {
            console.log("success")
        },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } 
            if (data) {
                return data
            }
        }
    })
}

export const useUpdateInternalUsersList = () => {
    return useMutation({
        mutationFn: async ({ id, data }) => {
            return await updateInternalUsersList(id, data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => {
            console.log("success")
        },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } 
            if (data) {
                return data
            }
        }
    })
}

export const useAddInternalUsersList = () => {
    return useMutation({
        mutationFn: async (data) => {
            return await addInternalUserList(data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => {
            console.log("success")
        },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const useAddPriceMatrixException = () => {
    return useMutation({
        mutationFn: async (data) => {
            return await addPriceMatrixException(data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => {
            console.log("success")
        },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } 
            if (data) {
                return data
            }
        }
    })
}
export const useAddMasterMappingTMC = () => {
    return useMutation({
        mutationFn: async (data) => {
            return await addMasterMappingTMC(data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => {
        },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}
export const useAddCosumerDocument = () => {
    return useMutation({
        mutationFn: async (data) => {
            return await API.addConsumerDocument(data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => {
        },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const useUpdatePriceMatrixException = () => {
    return useMutation({
        mutationFn: async ({ id, data }) => {
            return await updatePriceMatrixException(id, data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => {

        },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}
export const useUpdateMasterMappingTMC = () => {
    return useMutation({
        mutationFn: async ({ id, data }) => {
            return await updateMasterMappingTMC(id, data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => {
        },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } 
            if (data) {
                return data
            }
        }
    })
}
export const useUpdateConsumerDocument = () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await API.updateConsumerDocument(payload.ticket_id, payload.data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => {
        },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } 
            if (data) {
                return data
            }
        }
    })
}


export const useAddScheduleTraining = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (body) => {
            return await addTrainingSchedule(body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetTrainingList"]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: ["GetScheduledParticipantsList"]
                    })
                ])
            }
        }
    })
}

export const DownloadServiceCenterMutationRecord = (customsearchtext,
    brands,
    countries) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadServiceCenterRecord(customsearchtext,
                brands,
                countries).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}
/**
 * Service Center POST Create Address
 */

// Update Country Hub Asociation
export const CreateSCAddress = () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await createRecordList(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export function useUpdateTrainingCompletionStatus() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: (data) => updateTrainingCompletionStatus(data),
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetTrainingById"]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: ["GetTrainingList"]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: ["GetScheduledParticipantsList"]
                    })
                ])
            }
        }
    })
}

// Update Servive Center Address
export const UpdateServiceCenterAddress = () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await updateServiceCenter(payload.id, payload.data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } 
            if (data) {
                return data
            }
        }
    })
}

export const PostAllComment = () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await postAllComments(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } 
            if (data) {
                console.log(data)
            }
        }
    })
}

export const useAddNewParticipant = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await addNewParticipant(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (data) {
                return data
            }
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetTrainingById"]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: ["GetScheduledParticipantsList"]
                    })
                ])
            }
        }
    })
}

export const DownloadeParticipantsList = () => {
    return useMutation({
        mutationFn: async (id) => {
            return await downloadParticipantRecords(id).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export function useUpdateWorkListApproval() {
    return useMutation({
        mutationFn: async ({ id, data }) => {
            return await updateWorkListApproval(id, data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => console.log("Update is done"),
        onSuccess: () => { },
        onError: () => console.log("error occured while updating")
    })
}

export const UpdateParticipantComplitionStatus = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await updateParticipantCompletion(payload.id, payload.body).catch(
                (ex) => {
                    throw ex.response ? ex.response.data : ex.message
                }
            )
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (data) {
                return data
            }
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetTrainingById"]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: ["GetTrainingList"]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: ["GetScheduledParticipantsList"]
                    })
                ])
            }
        }
    })
}

export const GetScheduledParticipantsALL = () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await getScheduledParitcipants({
                queryKey: [
                    "GetScheduledParticipantsALL",
                    {
                        scheduledTrainingId: payload.scheduledTrainingId,
                        filter: payload.filter,
                        search: payload.search
                    }
                ]
            }).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

// Get User Details after passing email id and password
export const UserLoginDetails = () => {

    return useMutation({
        mutationFn: async ({ email, password, token,otp }) => {
            return await getUser({ email, password, token,otp }).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const internalUserDetails = () => {
    return useMutation({
        mutationFn: async ({ email, password, token,otp }) => {
            return await getInterUser({ email, password, token,otp }).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {-
            sessionStorage.setItem("loginDetails", "")
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const  ForgetPasswordRequest= () => {
    // const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.forgetPasswordRequest(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
        }
    })
}

export const UserLoginVerification= () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await API.userLoginVerification(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
        }
    })
}

export const ResetPassword= () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await API.resetPassword(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
        }
    })
}

// Notification list

export function UpdateNotificationAsRead() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (id) => {
            return await updateNotificationAsRead(id).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetUserNotificationList"]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: ["GetUserNotificationCount"]
                    })
                ])
            }
        }
    })
}


// finished Goods
export const useCreateFinsihedGood = () => {
    return useMutation({
        mutationFn: async (data) => {
            return await createFinsihedGood(data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const useUpdateFinishedGoodsByID = () => {
    return useMutation({
        mutationFn: async ({ ID, data }) => {
            return await updateFinishedGoodsByID(ID, data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}


/**
 * Type Number POST Create Type Number
 */

export const CreateTypeNumberRecord = () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await createTypeNumber(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}


/**
 * Type Number PATCH Update Type Number
 */

// Update Servive Center Address
export const UpdateTypeNumber = () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await updateTypeNumber(payload.id, payload.requestPayload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}
export const CreatePartRecord = () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await createPartNumber(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

/**
 * Complaints Without Technical Defects POST Create Complaints Without Technical Defects
 */

export const CreateComplaintsWithoutTechnicalDefectsRecord = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await createComplaintsWithoutTechnicalDefects(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetComplaintsWithoutTechnicalDefectsList"]
                    })
                ])
            }
        }
    })
}

/**
 * Complaints Without Technical Defects PATCH Update Complaints Without Technical Defects
 */
export const UpdateComplaintsWithoutTechnicalDefects = () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await updateComplaintsWithoutTechnicalDefects(payload.id, payload.requestBody).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

// Download Type Number List
export const DownloadTypeNumberList = (languageId, search,orderBy,orderByField,filterByCategory, filterBySubCategory, filterByActiveStatus) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadTypeNumberList(languageId, search,orderBy,orderByField, filterByCategory, filterBySubCategory, filterByActiveStatus).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

// Download Overview Product Catalog List
export const DownloadOverViewProductList = (parm) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadOverViewProductList(parm.languageId, parm.countryId, parm.currentTypeNoSpecific,parm?.orderBy,parm?.orderByField
            ).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

// Download Type Number List
export const DownloadFinishedGoodsList = (language_id, search, orderBy, orderByField, filterByCategory, filterBySubCategory, filterByInProgress, filterByfinishedActive, filterTypeNo, filterRegionID) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadFinishedGoodsList(language_id, search, orderBy, orderByField, filterByCategory, filterBySubCategory, filterByInProgress, filterByfinishedActive, filterTypeNo, filterRegionID).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}


// Download AAR Overview List
export const DownloadWarrantyWorkflowRules = (languageId, search, salesOrg, showOnlyActiveRules, isGlobal) => {
    return useMutation({
        mutationFn: async () => {
            return await API.downloadWarrantyWorkflowRules(languageId, search, salesOrg, showOnlyActiveRules, isGlobal).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

// download PS Indicator List
export const DownloadPsIndicatorList = (currentLanguage) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadPsIndicatorList(currentLanguage).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

// download Exception code code List 
export const DownloadExceptionCodeList = (currentLanguage) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadExceptionCodeList(currentLanguage).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        }
    })
}
// download Exception Approver List
export const DownloadExceptionApproverCodeList = (currentLanguage) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadExceptionApproverCodeList(currentLanguage).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        }
    })
}
// download Product form code List
export const DownloadProductFormList = (currentLanguage) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadProductFormList(currentLanguage).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        }
    })
}
// download failur Code List
export const DownloadFailureCodeList = (currentLanguage) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadFailureCodeList(currentLanguage).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}
// download GG Indicator List
export const DownloadGgIndicatorList = (currentLanguage) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadGgIndicatorList(currentLanguage).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

// download GG Indicator List
export const DownloadActionCodeList = (currentLanguage) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadActionCodeList(currentLanguage).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const DownloadPriceMatrixList = (column, order, customsearchtext, filter) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadPriceMatrixList(column, customsearchtext, filter, order).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const DownloadPriceEngineSimulation = () => {
    return useMutation({
        mutationFn: async (filter) => {
            return await API.downloadPriceEngineSimulation(filter).catch((ex) => {
                throw ex.response ? ex.response?.data : ex?.message
            })
        },
        onMutate: () => { },
        onError: () => { },
        onSuccess: () => { },
        onSettled: async () => {
        }
    })
}

export const DownloadPriceMatrixExceptionList = (customsearchtext, filter) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadPriceMatrixExceptionList(customsearchtext, filter).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const DownloadPartsList = (languageId, searchText,orderBy,orderByField, filterByMaterialClass, filterByProdLine, filterByActiveStatus) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadPartsList(languageId, searchText,orderBy,orderByField ,filterByMaterialClass, filterByProdLine, filterByActiveStatus).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const DownloadAssociatePartToType = (search,orderBy,orderByField, filterByTypeNumber, filterByMaterialClass, languageId) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadAssociatePartToType(search,orderBy,orderByField, filterByTypeNumber, filterByMaterialClass, languageId).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

// Download Examples For Complaints List
export const DownloadComplaintsWithoutTechnicalDefectsList = (languageId, search, orderBy, orderByField, internGroupId, internSubGroupId) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadComplaintsWithoutTechnicalDefectsList(languageId, search, orderBy, orderByField, internGroupId, internSubGroupId).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}
export const DownloadFGCountryList = (customsearchtext,orderBy, orderByField) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadFGAssociationCountryList(customsearchtext,orderBy, orderByField).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const UpdatePartRecord = () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await updatePartDetails(payload?.id, payload?.requestBody).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const UpdateReceipient = () => {
    return useMutation({
        mutationFn: async (body) => {
            return await updateReadRecipient(body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}
//part-typeNumber-association
export const UpdateAssociatePartToType = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await updateAssociatePartToTypeApi(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })

        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["getAssociatePartsTypeList"]
            })
        },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                return error
            }
            if (data) {
                return data
            }
        }
    })
}

export const UpdateTranslateRecordsPartsToType = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (updatedData) => await API.updateTranslateRecordsPartsToType(updatedData),
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["translateRecordsPartsToType"]
            })
            queryClient.invalidateQueries({
                queryKey: ["getAssociatePartsTypeList"]
            })
        },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                return error
            }
            if (data) {
                return data
            }
        }
    })
}

export const CreateAssociatePartToType = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await createPartTypeAssociationApi(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getAssociatePartsTypeList"] })
        },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}
// Delete Associate Parts of type
export const DeleteAssociatePartsOfType = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (id) => {
            return await deletAssociatepartOfType(id)
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getAssociatePartsTypeList"] })
        },
        retry: 1,
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }
        }
    })
}

export const CreateBlockTypeNoToCountry = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await createBlockTypeNoToCountry(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getTypeCountryBlockList"] })
        },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const CreateBlockBullitenToCountry = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await createBlockBullitenToCountry(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getBulletinCountryBlockList"] })

        },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export function DeletBlockType() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (data) => {
            return await deletBlockType(data.id).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getTypeCountryBlockList"] })
        },
        onError: async (error) => {
            if (error) {
                throw error
            }

        },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const WarrantyTicketStatusForApproval = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.warrantyTicketStatusForApproval(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getWarrantyList"] })

        },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const WarrantyPerformaStatusForApproval = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.warrantyPerformaStatusForApproval(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getWarrantyList"] })

        },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const WarrantyTicketForInvoiceProcess = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.warrantyTicketForInvoiceProcess(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getWarrantyList"] })

        },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const UpdateWarrantyTicketStatus = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.updateWarrantyTicketStatus(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getWarrantyList"] })

        },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export function DeletWarrantyTicket() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (data) => {
            return await API.deletWarrantyTicket(data.id).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getWarrantyList"] })
        },
        onError: async (error) => {
            if (error) {
                throw error
            }

        },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}




export function DeletBlockBulletin() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (data) => {
            return await deletBlockBulletin(data.id).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getBulletinCountryBlockList"] })
        },
        onError: async (error) => {
            if (error) {
                throw error
            }

        },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const DownloadBlockTypeNo = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async () => {
            return await downloadFGAssociationCountryList().catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["getTypeCountryBlockList"] })
        },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

// Create  Service Material Association
export const CreateServiceMaterialAssociation = () => {
    return useMutation({
        mutationFn: async (body) => {
            return await createAssociationForServiceMaterial(body, "TypeNoAndActionCode").catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } 
            if (data) {
                return data
            }
        }
    })
}
// Update Failure Code Association
export const UpdateServiceMaterialAssociation = () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await updateAssociationForServiceMaterial(payload.id, payload.data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } 
            if (data) {
                return data
            }
        }
    })
}

export const DeleteServiceMaterialAssociation = () => {
    return useMutation({
        mutationFn: async (id) => {
            return await deleteAssociationForServiceMaterial(id)
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => {
            console.log("success")
        },
        retry: 3,
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }
        }
    })
}

export const CreateAssociationFGWithCountry = () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await createAssociationFGWithCountry(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const DeleteComplaintsWithoutTechnicalDefects = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (id) => {
            return await deleteComplaintsWithoutTechnicalDefects(id)
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["GetComplaintsWithoutTechnicalDefectsList"] })
        },
        retry: 3,
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }
        }
    })
}

// Uploader
export const UploadFilesToModule = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (fileData) => {
            return await saveUploadedData(fileData).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: (async (data, error) => {
            if (error) {
                throw error
            } else {
                queryClient.invalidateQueries({ queryKey: ["GetUploadedFilesByModule"] })
            }
            if (data) {
                return data
            }
        }),
        onSuccess: () => { },
        retry: 1
    })
}

export const UploadFilesToModuleV2 = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (fileData) => {
            return await API.saveUploadedDataV2(fileData).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: (async (data, error) => {
            if (error) {
                throw error
            } else {
                queryClient.invalidateQueries({ queryKey: ["GetUploadedFilesByModuleV2"] })
            }
            if (data) {
                return data
            }
        }),
        onSuccess: () => { },
        retry: 1
    })
}

// Create Article Series
export const CreateArticleSeries = () => {
    return useMutation({
        mutationFn: async (body) => {
            return await createArticle(body).catch((ex) => {
                throw ex.response ? ex?.response?.data : ex?.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } 
            if (data) {
                return data
            }
        }
    })
}

export const UpdateArticleSeries = () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await updateArticleSeries(payload.id, payload.data).catch((ex) => {
                throw ex?.response ? ex?.response?.data : ex?.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } 
            if (data) {
                return data
            }
        }
    })
}

export const DeleteArticleSeries = () => {
    return useMutation({
        mutationFn: async (id) => {
            return await deleteArticleSeries(id)
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => {
            console.log("success")
        },
        retry: 3,
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }
        }
    })
}

export const DownloadServiceMaterialAssociation = (customsearchtext, appliedFilterOptions) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadServiceMaterialAssociation(customsearchtext, appliedFilterOptions).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const DownloadArticleSeries = (customsearchtext) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadArticleSeries(customsearchtext).catch((ex) => {
                throw ex?.response ? ex?.response?.data : ex?.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const DownloadLifeCycleStatus = (search, filterBySalesOrg, filterByPartNo, filterByActiveStatus, filterByManualFlag,orderBy,orderByField) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadLifeCycleStatusList(search, filterBySalesOrg, filterByPartNo, filterByActiveStatus, filterByManualFlag,orderBy,orderByField).catch((ex) => {
                throw ex?.response ? ex?.response?.data : ex?.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

// Service material Associations 

export const useAddServiceMaterial = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (body) => {
            return await createServiceMaterialAssociationList(body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetServiceMaterialAssociations"]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: ["GetMaterialOptions"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

export const useAddServiceMaterialSalesOrg = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (body) => {
            return await createServiceMaterialAssociationSalesOrg(body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetServiceMaterialAssociations"]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: ["GetMaterialOptions"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

export const useAddServiceMaterialTranslations = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await createServiceMaterialAssociationTranslations(payload.body, payload.id).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetServiceMaterialAssociations"]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: ["GetMaterialOptions"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

export const UpdateServiceMaterialSalesOrg = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (body) => {
            return await updateServiceMatrialAssociations(body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetServiceMaterialAssociations"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}


export const DeleteProductLifecycle = () => {
    return useMutation({
        mutationFn: async (id) => {
            return await deleteProductLifecycle(id).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }
        }
    })
}

export const DownloadPriceDerivation = (parm) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadPriceDerivation(parm).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        }
    })
}

export const DownloadOriginalNOS = (parm) => {
    return useMutation({
        mutationFn: async () => {
            return await API.downloadOriginalNOS(parm).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        }
    })
}

export function useUpdatePriceDerivation() {
    return useMutation({
        mutationFn: async (data) => await UpdatePriceDerivation(data),
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }
        }
    })
}

export function useUpdateOriginalNOS() {
    return useMutation({
        mutationFn: async (data) => await API.UpdateOriginalNOS(data),
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }
        }
    })
}

export function useUpdateOriginalNOSCurrency() {
    return useMutation({
        mutationFn: async (data) => await API.UpdateOriginalNOSCurrency(data),
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }
        }
    })
}

export const useCreateFgPartNoAssociation = () => {
    const queryClient = useQueryClient()
    return useMutation({
        // mutationFn: async (payload) => await createPriceMatrixApi(payload),
        mutationFn: async (payload) => await createFgPartNoAssociationApi(payload),
        onMutate: () => { },
        onError: () => {
            // throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["associatedTypeList"]
            })
        },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const DeleteAssociationOfFgWithParts = () => {
    return useMutation({
        mutationFn: async (id) => {
            return await deleteAssociationOfFgWithParts(id)
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => {
            console.log("success")
        },
        retry: 3,
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }
        }
    })
}

/* Update HotSpot*/
export const useUpdateHotSpot = () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await API.updateHotSpot(payload.id, payload.body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return data
            }
        }
    })
}

/* Remove HotSpot*/
export const useRemoveHotSpot = () => {
    return useMutation({
        mutationFn: async (id) => {
            return await API.deleteHotSpot(id).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return data
            }
        }
    })
}


/* Remove HotSpot Image*/
export const useRemoveHotSpotImage = () => {
    return useMutation({
        mutationFn: async (id) => {
            return await API.deleteHotSpotImage(id).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return data
            }
        }
    })
}

export const useUpdateHotSpotImage = () => {
    return useMutation({
        mutationFn: async (id) => {
            return await API.updateHotSpotImage(id).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return data
            }
        }
    })
}

// Download Spare Parts List
export const DownloadSpareParts = (languageId, typeId, typeRecords) => {
    return useMutation({
        mutationFn: async () => {
            return await API.downloadSpareParts(languageId, typeId, typeRecords).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const DownloadAssociatedPartList = (search,orderBy,orderByField, filterByTypeNumber, filterByIsAssociatedFlag, filterByFpcNumber, languageId) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadAssociatedPartList(search,orderBy,orderByField, filterByTypeNumber, filterByIsAssociatedFlag, filterByFpcNumber, languageId
            ).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const FilterAssociatedPartList = (parm) => {
    return useMutation({
        mutationFn: async () => {
            return await filterAssociatedPartList(parm.type_id, parm.finished_goods_id, parm.is_associated
            ).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const FilterAssociatedFgList = (parm) => {
    return useMutation({
        mutationFn: async () => {
            return await filterAssociatedFgList(parm.type_id, parm.part_id, parm.is_associated
            ).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const DownloadAssociatedFgList = (search, orderBy, orderByField, filterByTypeNumber, filterByIsAssociatedFlag, filterByPartNumber, languageId) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadAssociatedFgList(search, orderBy, orderByField, filterByTypeNumber, filterByIsAssociatedFlag, filterByPartNumber, languageId
            ).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const GetFinishedGoodsListAll = (parm) => {
    return useMutation({
        mutationFn: async () => {
            return await getFinishedGoodsListAll(parm.type_id
            ).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const DownloadPriceList = (customsearchtext, filter, orderBy, orderByField) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadPriceList(customsearchtext, filter, orderBy, orderByField).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const DownloadMonthlyRateList = () => {
    return useMutation({
        mutationFn: async () => {
            return await API.downloadMonthlyRateList().catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const DownloadFirmRateList = () => {
    return useMutation({
        mutationFn: async () => {
            return await API.downloadFirmRateList().catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

// Parcel Condition save 
export const UseAddParcelCondition = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.createParcelCondition(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetParcelConditionList"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

export const UseUpdateParcelCondition = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.updateParcelCondition(payload.id, payload.body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetParcelConditionList"]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: ["GetParcelConditionById"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

// Block Maintenance save 
export const UseAddBlockMaintenance = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.createBlockMaintenance(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetBlockMaintenanceList"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

export const UseUpdateBlockMaintenance = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.updateBlockMaintenance(payload.id, payload.body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetBlockMaintenanceList"]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: ["GetBlockMaintenanceById"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

// Parcel Content Status save 
export const UseAddParcelContentStatus = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.createParcelContentStatus(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetParcelContentStatusList"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

export const UseUpdateParcelContentStatus = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.updateParcelContentStatus(payload.id, payload.body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetParcelContentStatusList"]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: ["GetParcelContentStatusById"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

// Retailer Name save 
export const UseAddRetailer = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.createRetailer(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetRetailerList"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

export const UseUpdateRetailer = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.updateRetailer(payload.id, payload.body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetRetailerList"]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: ["GetRetailerDetailsById"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

export const UpdateNOSTMCPrice = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.updateNOSTMCPriceApi(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })

        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["listNosTMCPrice"]
            })
        },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                return error
            }
            if (data) {
                return data
            }
        }
    })
}

export const DownloadNosTMCPriceList = (customsearchtext, filter) => {
    return useMutation({
        mutationFn: async () => {
            return await API.downloadNosTMCPriceList(customsearchtext, filter).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}


// Contry Attribute Config - Address Validation 
export const UseAddAddressValidationConfig = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.createAddressValidationConfig(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetAddressConfigurationList"]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: ["GetAddressConfigurationByCountryId"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

export const UseUpdateAddressValidationConfig = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.updateAddressValidationConfig(payload.id, payload.body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetAddressConfigurationList"]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: ["GetAddressConfigurationByCountryId"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

//warrenty attribute

export const UseAddWarrantyValidationConfig = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.createWarrantyValidationConfig(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetWarrantyConfigurationList"]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: ["GetWarrantyConfigurationByCountryId"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

export const UseUpdateWarrantyValidationConfig = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.updateWarrantyValidationConfig(payload.id, payload.body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetWarrantyConfigurationList"]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: ["GetWarrantyConfigurationByCountryId"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}


// Warranty Download Bulk Ticket List
export const DownLoadBulkTicketList = (languageId, bulkId) => {
    return useMutation({
        mutationFn: async () => {
            return await API.downloadBulkTicketList(languageId, bulkId).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const GetTroubleShootSubCategoriesById = () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await API.getTroubleShootCategoriesById(payload?.page, payload?.limit, payload?.typeNo, 
                payload?.categoryName, payload?.locale, payload?.irisCodeId).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } 
            if (data) {
                return data
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

export const CreateAutoApprovalRule = () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await API.createAutoApprovalRule(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const UpdateAutoApprovalRule = () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await API.updateAutoApprovalRule(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

// Warranty Policy save 
export const UseAddWarrantyPolicy = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.createWarrantyPolicy(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetWarrantyPolicyList"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

export const UseUpdateWarrantyPolicy = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.updateWarrantyPolicy(payload.id, payload.body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetWarrantyPolicyList"]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: ["GetWarrantyPolicyById"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

export const UseUpdateWarrantyPolicyActiveStatus = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.updateWarrantyPolicyActiveStatus(payload.id, payload.body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetWarrantyPolicyList"]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: ["GetWarrantyPolicyById"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

export const UseRemoveWarrantyPolicy = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (id) => {
            return await API.removeWarrantyPolicy(id).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetWarrantyPolicyList"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

export const CreateSecondarySCAddress = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.createSecondarySCAddress(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["orderAddressList"]
                    })
                ])
            }
        }
    })
}

export const UpdateAddressDetails = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.updateAddressDetails(payload?.id, payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["orderAddressList"]
                    })
                ])
            }
        }
    })
}

export const DeleteOrderAddress = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (id) => {
            return await API.deleteOrderAddress(id)
        },
        onMutate: () => { },
        onError: () => {
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["orderAddressList"] })
        },
        retry: 1,
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }
        }
    })
}

export const SendClaimForm = () => {
    // const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.sendClaimForm(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            // else {
            //     return await Promise.all([
            //         queryClient.invalidateQueries({
            //             queryKey: ["orderAddressList"]
            //         })
            //     ])
            // }
        }
    })
}

export const UseGetInvoiceHistory = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.getInvoiceHistory(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetWarrantyPolicyList"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

export const CreateAllowedMaterials = () => {
    return useMutation({
        mutationFn: async (body) => {
            return await createAllowedMaterials(body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const UpdateAllowedMaterials = () => {
    return useMutation({
        mutationFn: async (payload) => {
            return await updateAllowedMaterials(payload?.id, payload.data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } 
            if (data) {
                return data
            }
        }
    })
}

export const DeleteAllowedMaterials = () => {
    return useMutation({
        mutationFn: async (id) => {
            return await deleteAllowedMaterials(id)
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => {
            console.log("success")
        },
        retry: 3,
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }
        }
    })
}

export const DownloadAllowedMaterials = (customsearchtext,salesOrgAfterSelection) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadAllowedMaterials(customsearchtext,salesOrgAfterSelection).catch((ex) => {
                throw ex?.response ? ex?.response?.data : ex?.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

// Reject Maintenance save 
export const UseAddRejectMaintenance = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.createRejectMaintenance(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetRejectMaintenanceList"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}



export const UseUpdateRejectMaintenance = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.updateRejectMaintenance(payload.id, payload.body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetRejectMaintenanceList"]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: ["GetRejectMaintenanceById"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

export const DeletePriceMatrix = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (id) => {
            await API.deletPriceMatrix(id)
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["priceMatrixExceptionList"] })
        },
        retry: 1,
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }
        }
    })
}

export function CreateTypeOfRepair() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (data) => {
            return await API.createTypeOfRepair(data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => console.log("Create is done"),
        onSuccess: async () => {
        },
        onError: (e) => console.log("error occured while creating", e),
        onSettled: async (data, error) => {
            if(error) {
                throw error
            } else {
                queryClient.invalidateQueries({
                    queryKey: ["getTypeOfRepairList"]
                })
            }
        }
    })
}

export function UpdateTypeOfRepair() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ id, data }) => {
            return await API.updateTypeOfRepair(id, data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => console.log("Update is done"),
        onSuccess: async () => {
        },
        onError: (e) => console.log("error occured while updating", e),
        onSettled: async (data, error) => {
            if(error) {
                throw error
            } else {
                queryClient.invalidateQueries({
                    queryKey: ["getTypeOfRepairList"]
                })
            }
        }
    })
}

export function CreateReasonForImpossibility() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (data) => {
            return await API.createReasonForImpossibility(data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => console.log("Create is done"),
        onSuccess: async () => {
        },
        onError: (e) => console.log("error occured while creating", e),
        onSettled: async (data, error) => {
            if(error) {
                throw error
            } else {
                queryClient.invalidateQueries({
                    queryKey: ["getReasonForImpossibilityList"]
                })
            }
        }
    })
}

export function UpdateReasonForImpossibility() {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async ({ id, data }) => {
            return await API.updateReasonForImpossibility(id, data).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => console.log("Update is done"),
        onSuccess: async () => {
        },
        onError: (e) => console.log("error occured while updating", e),
        onSettled: async (data, error) => {
            if(error) {
                throw error
            } else {
                queryClient.invalidateQueries({
                    queryKey: ["getReasonForImpossibilityList"]
                })
            }
        }
    })
}

// Download Sample Collection List
export const DownloadSampleCollection = (language_id, country_id) => {
    const queryKey = ["", language_id, country_id]
    return useMutation({
        mutationFn: async () => {
            return await API.getSampleCollectionDataDownload({ queryKey }).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const GetAllCountryConfigList = () => {
    return useMutation({
        mutationFn: async () => {
            return await downloadCountryConfigRecords().catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

// Sample Collection 
export const UseAddSampleCollection = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payloadAsForm) => {
            return await API.saveSampleCollection(payloadAsForm).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetSampleCollectionList"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

export const UseUpdateSampleCollection = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.updateSampleCollection(payload.id, payload.body).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetSampleCollectionList"]
                    }),
                    queryClient.invalidateQueries({
                        queryKey: ["GetSampleCollectionById"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}

export const UseRemoveSampleCollection = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (id) => {
            return await API.removeSampleCollection(id).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onError: (error) => {
            throw error
        },
        onMutate: () => { },
        onSettled: async (data, error) => {
            if (error) {
                throw error
            } else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetSampleCollectionList"]
                    })
                ])
            }
        },
        onSuccess: () => { },
        retry: 1
    })
}
export const useCreatePerformanceConfig = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (data) => {
            return await CreatePerformanceConfig(data)
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["getPayForPerformacePCM"]
            })
        },
        retry: 0,
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }else{
                queryClient.invalidateQueries({
                    queryKey: ["getPayForPerformacePCM"]
                })
            }
        }
    })
}

export const useUpdatePerformanceConfig = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (data) => {
            return await updatePerformanceConfig(data.payload,data.id)
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => {
            console.log("success")
        },
        retry: 3,
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }else{
                queryClient.invalidateQueries({
                    queryKey: ["getPayForPerformacePCM"]
                })
            }
        }
    })
}

export const CreatePFPReport = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.createPFPReport(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetCreateReportList"]
                    })
                ])
            }
        }
    })
}

export const DownloadPFPConfig = (customsearchtext ) => {
    return useMutation({
        mutationFn: async () => {
            return await API.downloadPFPConfigList(customsearchtext).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}
export const DownloadPayPerfomenceDetails = (customsearchtext, salesOrgCode, from_period, to_period ) => {
    return useMutation({
        mutationFn: async () => {
            return await API.downloadPayPerfomenceDetails(customsearchtext, salesOrgCode, from_period, to_period).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const CreatePFPDetailsReport = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (payload) => {
            return await API.createPFPDetailReport(payload).catch((ex) => {
                throw ex.response ? ex.response.data : ex.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 0,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            else {
                return await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: ["GetPFPDetailReportList2"]
                    })
                ])
            }
        }
    })
}

export const useUpdateActiveStatus = () => {
    const queryClient = useQueryClient()
    return useMutation({
        mutationFn: async (data) => {
            return await updateActiveStatus(data.payload,data.id)
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => {
            console.log("success")
        },
        retry: 3,
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }else{
                queryClient.invalidateQueries({
                    queryKey: ["getPayForPerformacePCM"]
                })
            }
        }
    })
}

export const DownloadPFPReportList = (customsearchtext, filter) => {
    return useMutation({
        mutationFn: async () => {
            return await downloadPFPReport(customsearchtext, filter).catch((ex) => {
                throw ex?.response ? ex?.response?.data : ex?.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export const DeletePriceList = () => {
    return useMutation({
        mutationFn: async (id) => {
            await API.deletePriceList(id)
        },
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => {
            //queryClient.invalidateQueries({ queryKey: ["priceList"] })
        },
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }
        }
    })
}

export const DownloadListPriceUpload = (search, filter) => {
    return useMutation({
        mutationFn: async () => {
            return await API.downloadListPrice(search, filter).catch((ex) => {
                throw ex?.response ? ex?.response?.data : ex?.message
            })
        },
        onMutate: () => { },
        onError: (error) => {
            throw error
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (data, error) => {
            if (error) {
                throw error
            }
            if (data) {
                return data
            }
        }
    })
}

export function useUpdateListPrice(data) {
    return useMutation({
        mutationFn: async (data) => await API.UpdateListPrice(data),
        onMutate: () => { },
        onError: () => {
            console.log("error")
        },
        onSuccess: () => { },
        retry: 1,
        onSettled: async (_, error) => {
            if (error) {
                console.log(error)
            }
        }
    })
}